import React, { useState } from 'react';
import type {
  BuildCheck,
  BuildSummary,
  Decision,
  SecurityApplied,
  SecurityBucket,
  SecurityFinding,
  SecuritySection,
  SecuritySeverity,
  SecurityVerification,
  SpecSummary,
} from '../../../lib/aiJobSummary';
import { Badge, Icon } from '../../ui';
import type { BadgeTone } from '../../ui';
import SectionHeading, { OpCard } from './SectionHeading';

/**
 * Security Audit jobs: the spec as one report. Counts on top, then Security and Integrity grouped by bucket, one
 * compact row per finding with its Fix / Skip / Later answer inline (the same decision answer as everywhere else),
 * proof / fix / could-break behind a click. Parked lines sit collapsed at the end.
 */

const SEVERITY_LABEL: Record<SecuritySeverity, string> = { public: 'Public', login: 'Needs a login', data_loss: 'Data loss' };
const SEVERITY_TONE: Record<SecuritySeverity, BadgeTone> = { public: 'down', login: 'warn', data_loss: 'info' };

const BUCKETS: Record<SecuritySection, { id: SecurityBucket; label: string }[]> = {
  security: [
    { id: 'codebase', label: 'Codebase' },
    { id: 'database', label: 'Database' },
    { id: 'live_config', label: 'Live config' },
  ],
  integrity: [
    { id: 'cascade', label: 'Cascading deletes' },
    { id: 'drift', label: 'Migration drift' },
    { id: 'backups', label: 'Backups' },
  ],
};

const APPLIED: Record<SecurityApplied, { label: string; tone: BadgeTone }> = {
  live: { label: 'Live', tone: 'up' },
  pr: { label: 'In a PR', tone: 'info' },
  rolled_back: { label: 'Rolled back', tone: 'down' },
  skipped: { label: 'Skipped', tone: 'idle' },
};

const eyebrowStyle: React.CSSProperties = { marginBottom: '4px' };
const bodyText: React.CSSProperties = { fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-normal)', color: 'var(--text-body)' };
const mono: React.CSSProperties = {
  margin: 0,
  padding: '8px 10px',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--surface-canvas)',
  border: '1px solid var(--border-hairline)',
  fontFamily: 'var(--font-mono)',
  fontSize: 'var(--text-xs)',
  lineHeight: 1.5,
  color: 'var(--text-body)',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

export default function SecurityAuditReport({
  spec,
  build,
  readOnly = false,
  onAnswer,
}: {
  spec: SpecSummary;
  build?: BuildSummary | null;
  readOnly?: boolean;
  onAnswer?: (decisionId: string, answer: string | null) => Promise<void>;
}) {
  const findings = spec.findings ?? [];
  const decisions = new Map(spec.decisions.map((d) => [d.id, d]));
  const checks = new Map((build?.checks ?? []).map((c) => [c.request, c]));
  const count = (s: SecuritySeverity) => findings.filter((f) => f.severity === s).length;
  const open = findings.filter((f) => !decisions.get(f.decision_id)?.answer?.trim()).length;

  return (
    <>
      <OpCard padding="16px 18px">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '30px',
                height: '30px',
                borderRadius: 'var(--radius-sm)',
                background: count('public') ? 'var(--status-down-soft)' : 'var(--status-up-soft)',
                color: count('public') ? 'var(--status-down)' : 'var(--status-up)',
                flexShrink: 0,
              }}
            >
              <Icon name={count('public') ? 'shield-alert' : 'shield-check'} size={16} />
            </span>
            <div style={{ minWidth: 0 }}>
              <div className="t-eyebrow" style={eyebrowStyle}>
                Security audit · {findings.length} {findings.length === 1 ? 'finding' : 'findings'}
              </div>
              <div style={{ fontSize: 'var(--text-md)', fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'], letterSpacing: 'var(--tracking-tight)', color: 'var(--text-body)' }}>
                {open ? `${open} ${open === 1 ? 'needs' : 'need'} your call` : 'Every finding answered'}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap' }}>
            {(['public', 'login', 'data_loss'] as SecuritySeverity[]).map((s) => (
              <div key={s} style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '64px' }}>
                <span className="t-num" style={{ fontSize: 'var(--text-xl)', lineHeight: 1, color: count(s) ? `var(--status-${SEVERITY_TONE[s]})` : 'var(--text-faint)' }}>
                  {count(s)}
                </span>
                <span className="t-eyebrow">{SEVERITY_LABEL[s]}</span>
              </div>
            ))}
          </div>
        </div>
      </OpCard>

      {(['security', 'integrity'] as SecuritySection[]).map((section) => {
        const mine = findings.filter((f) => f.section === section);
        return (
          <section key={section}>
            <SectionHeading
              eyebrow={section === 'security' ? 'What a stranger can do' : 'What could lose data'}
              title={section === 'security' ? 'Security' : 'Integrity'}
              icon={section === 'security' ? 'lock' : 'database'}
              count={mine.length}
            />
            <OpCard padding="4px 0">
              {BUCKETS[section].map((b, bi) => {
                const rows = mine.filter((f) => f.bucket === b.id);
                return (
                  <div key={b.id} style={{ borderTop: bi === 0 ? 'none' : '1px solid var(--border-hairline)' }}>
                    <div className="t-eyebrow" style={{ padding: '10px 16px 4px', display: 'flex', gap: '8px' }}>
                      <span>{b.label}</span>
                      <span style={{ color: 'var(--text-faint)' }}>{rows.length || ''}</span>
                    </div>
                    {rows.length ? (
                      rows.map((f) => (
                        <FindingRow key={f.id} finding={f} decision={decisions.get(f.decision_id)} check={checks.get(f.id)} readOnly={readOnly || !onAnswer} onAnswer={onAnswer} />
                      ))
                    ) : (
                      <div style={{ padding: '2px 16px 12px', fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>Nothing proven.</div>
                    )}
                  </div>
                );
              })}
            </OpCard>
          </section>
        );
      })}

      {spec.parked.length ? <ParkedList items={spec.parked} /> : null}
    </>
  );
}

function FindingRow({
  finding: f,
  decision,
  check,
  readOnly,
  onAnswer,
}: {
  finding: SecurityFinding;
  decision?: Decision;
  check?: BuildCheck;
  readOnly: boolean;
  onAnswer?: (decisionId: string, answer: string | null) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ padding: '8px 16px 10px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="pressable"
          style={{ flex: '1 1 320px', minWidth: 0, display: 'flex', gap: '10px', alignItems: 'flex-start', background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', color: 'inherit' }}
        >
          <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} style={{ color: 'var(--text-faint)', marginTop: '3px', flexShrink: 0 }} />
          <span style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                {f.id}
              </span>
              <Badge tone={SEVERITY_TONE[f.severity]}>{SEVERITY_LABEL[f.severity]}</Badge>
              {f.cascade ? <Badge tone={f.cascade.label === 'good' ? 'up' : 'down'}>{f.cascade.label === 'good' ? 'Good cascade' : 'Bad cascade'}</Badge> : null}
              {check?.applied ? <Badge tone={APPLIED[check.applied].tone} dot>{APPLIED[check.applied].label}</Badge> : null}
            </span>
            <span style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'], color: 'var(--text-body)', lineHeight: 'var(--leading-snug)' }}>{f.title}</span>
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 'var(--leading-snug)' }}>{f.exposed}</span>
          </span>
        </button>
        {decision ? <InlineDecision decision={decision} readOnly={readOnly} onAnswer={onAnswer} /> : null}
      </div>

      {open ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', margin: '12px 0 4px 24px' }}>
          <div className="ck-op-pair">
            <div style={{ minWidth: 0 }}>
              <div className="t-eyebrow" style={eyebrowStyle}>
                Request
              </div>
              <pre style={mono}>{f.proof.request}</pre>
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="t-eyebrow" style={eyebrowStyle}>
                What came back
              </div>
              <pre style={mono}>{f.proof.result}</pre>
            </div>
          </div>
          {f.cascade ? <CascadeDetail cascade={f.cascade} /> : null}
          <div className="ck-op-pair">
            <div>
              <div className="t-eyebrow" style={eyebrowStyle}>
                The fix
              </div>
              <div style={bodyText}>{f.fix}</div>
            </div>
            <div>
              <div className="t-eyebrow" style={eyebrowStyle}>
                Could break
              </div>
              <div style={bodyText}>{f.could_break}</div>
            </div>
          </div>
          {check ? (
            <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--surface-raised)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div className="t-eyebrow">Build</div>
              <div style={bodyText}>
                <span style={{ color: 'var(--text-faint)' }}>Before: </span>
                {check.before}
              </div>
              <div style={bodyText}>
                <span style={{ color: 'var(--text-faint)' }}>After: </span>
                {check.after}
              </div>
              <div style={{ ...bodyText, color: 'var(--text-muted)' }}>
                <span style={{ color: 'var(--text-faint)' }}>Check: </span>
                {check.how_to_check}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function CascadeDetail({ cascade }: { cascade: NonNullable<SecurityFinding['cascade']> }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <div className="t-eyebrow">Cascade</div>
      <div style={bodyText}>
        <span className="t-num">{cascade.from_table}</span> → <span className="t-num">{cascade.to_table}</span>
        <span style={{ color: 'var(--text-faint)' }}> · {cascade.constraint_name}</span>
      </div>
      <div style={{ ...bodyText, color: 'var(--text-muted)' }}>{cascade.why}</div>
      <div style={bodyText}>
        <span style={{ color: 'var(--text-faint)' }}>One delete of {cascade.delete_count.sample} takes: </span>
        {cascade.delete_count.tables.map((t, i) => (
          <span key={t.table} className="t-num">
            {i ? ', ' : ''}
            {t.table} {t.rows}
          </span>
        ))}
      </div>
    </div>
  );
}

/** The finding's decision, answered on the row. Same write as the decisions in "What you need to know". */
function InlineDecision({
  decision,
  readOnly,
  onAnswer,
}: {
  decision: Decision;
  readOnly: boolean;
  onAnswer?: (decisionId: string, answer: string | null) => Promise<void>;
}) {
  const [saving, setSaving] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const answer = decision.answer?.trim() || null;
  const save = async (value: string | null) => {
    if (!onAnswer || readOnly || saving) return;
    setSaving(value ?? '');
    setFailed(false);
    try {
      await onAnswer(decision.id, value);
    } catch {
      setFailed(true);
    } finally {
      setSaving(null);
    }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0, marginLeft: 'auto' }}>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {(decision.options ?? []).map((opt) => {
          const selected = answer === opt;
          return (
            <button
              key={opt}
              type="button"
              className={`ck-op-option pressable${selected ? ' is-selected' : ''}`}
              disabled={readOnly || saving !== null}
              aria-pressed={selected}
              title={selected ? 'Click again to un-answer' : `${decision.id}: ${opt}`}
              onClick={() => void save(selected ? null : opt)}
              style={{ height: '26px', padding: '0 10px' }}
            >
              {selected ? <Icon name="check" size={12} strokeWidth={2.25} /> : null}
              {opt}
              {decision.recommendation === opt && !selected ? (
                <span className="t-eyebrow" style={{ fontSize: '9px', color: 'var(--accent-text)', letterSpacing: '0.06em' }}>
                  rec
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      {failed ? (
        <Badge tone="down" dot>
          Not saved
        </Badge>
      ) : !answer && decision.why ? (
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', maxWidth: '300px', textAlign: 'right', lineHeight: 'var(--leading-snug)' }}>{decision.why}</span>
      ) : null}
    </div>
  );
}

function ParkedList({ items }: { items: string[] }) {
  const [open, setOpen] = useState(false);
  return (
    <section>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="pressable"
        style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-muted)' }}
      >
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} />
        <span className="t-eyebrow">Parked · {items.length} minor or unlikely, no call needed</span>
      </button>
      {open ? (
        <OpCard tone="raised" padding="6px 16px" >
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {items.map((p, i) => (
              <li key={i} style={{ padding: '8px 0', borderTop: i ? '1px solid var(--border-hairline)' : 'none', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 'var(--leading-snug)' }}>
                {p}
              </li>
            ))}
          </ul>
        </OpCard>
      ) : null}
    </section>
  );
}

/** Security Audit builds: proof the fixes broke nothing. Green only when the snapshot passed and no test user is left. */
export function SecurityVerificationCard({ verification: v }: { verification: SecurityVerification }) {
  const good = v.snapshot.passed && v.test_users_left === 0 && v.walkthroughs.every((w) => w.passed);
  const tone = good ? 'up' : 'down';
  const rolled = new Set(v.rolled_back);
  return (
    <OpCard padding="16px 18px">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '30px',
              height: '30px',
              borderRadius: 'var(--radius-sm)',
              background: `var(--status-${tone}-soft)`,
              color: `var(--status-${tone})`,
              flexShrink: 0,
            }}
          >
            <Icon name={good ? 'shield-check' : 'shield-alert'} size={16} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div className="t-eyebrow" style={eyebrowStyle}>
              Verification
            </div>
            <div style={{ fontSize: 'var(--text-md)', fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'], letterSpacing: 'var(--tracking-tight)', color: 'var(--text-body)' }}>
              {good ? 'Nothing broke' : 'Needs a look before you approve'}
            </div>
          </div>
        </div>
        <Badge tone={v.test_users_left === 0 ? 'up' : 'down'} dot>
          {v.test_users_created} test {v.test_users_created === 1 ? 'user' : 'users'} · {v.test_users_left} left
        </Badge>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px', marginTop: '14px' }}>
        <div>
          <div className="t-eyebrow" style={{ marginBottom: '6px' }}>
            Snapshot before and after
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <Badge tone={v.snapshot.passed ? 'up' : 'down'} dot>
              {v.snapshot.passed ? 'Same' : 'Differs'}
            </Badge>
            <span className="t-num" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
              {v.snapshot.pages} pages · {v.snapshot.queries} queries by role
            </span>
          </div>
          {v.snapshot.diffs.map((d, i) => (
            <div key={i} style={{ marginTop: '8px', fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-snug)' }}>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--text-body)' }}>{d.what}</span>
                {rolled.has(d.what) ? <Badge tone="info">rolled back</Badge> : <Badge tone="down">not rolled back</Badge>}
              </div>
              <div className="t-num" style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>
                {d.before} → {d.after}
              </div>
            </div>
          ))}
        </div>
        <div>
          <div className="t-eyebrow" style={{ marginBottom: '6px' }}>
            Walkthroughs by role
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            {v.walkthroughs.map((w) => (
              <div key={w.role} style={{ fontSize: 'var(--text-sm)' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Icon name={w.passed ? 'check' : 'x'} size={13} strokeWidth={2.25} style={{ color: w.passed ? 'var(--status-up)' : 'var(--status-down)', flexShrink: 0 }} />
                  <span style={{ color: 'var(--text-body)' }}>{w.role}</span>
                  <span className="t-num" style={{ color: 'var(--text-faint)', fontSize: 'var(--text-xs)', whiteSpace: 'nowrap' }}>
                    {w.steps} steps
                  </span>
                </div>
                {w.note ? <div style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)', paddingLeft: '21px', lineHeight: 'var(--leading-snug)' }}>{w.note}</div> : null}
              </div>
            ))}
          </div>
        </div>
        {v.not_tested_email.length ? (
          <div>
            <div className="t-eyebrow" style={{ marginBottom: '6px' }}>
              Not tested · sends email
            </div>
            <ul style={{ margin: 0, paddingLeft: '16px', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 'var(--leading-snug)' }}>
              {v.not_tested_email.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {v.rolled_back.length ? (
          <div>
            <div className="t-eyebrow" style={{ marginBottom: '6px' }}>
              Rolled back
            </div>
            <ul style={{ margin: 0, paddingLeft: '16px', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 'var(--leading-snug)' }}>
              {v.rolled_back.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </OpCard>
  );
}
